import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import {
  ShieldCheck, IndianRupee, Zap, PhoneCall, BadgeCheck,
  ArrowRight, Sparkles, CheckCircle2, Camera, MapPin, Rocket,
  MessagesSquare, CalendarCheck, TrendingUp,
} from "lucide-react";

/**
 * OwnersLandingPage — dedicated landlord acquisition page for Google Ads traffic.
 *
 * Design intent (per Feb 2026 Landlord Conversion Brief):
 *   • ZERO tenant-search UI (no search bar, no discover reels, no property grid).
 *     Ad traffic bounces when landlords land on a tenant hero.
 *   • ONE hero, ONE promise, ONE CTA — "Post FREE Listing".
 *   • CTA fires `post_listing_clicked` on GTM dataLayer for Google Ads conversion tracking.
 *   • Reinforces: zero brokerage, verified badge, direct tenant calls, free forever.
 */
export default function OwnersLandingPage() {
  const nav = useNavigate();

  const postListing = (source) => {
    // GTM top-of-funnel intent event — powers Google Ads "Post Listing Click"
    // conversion. Slice by `source` (hero vs sticky vs bottom) to see which
    // CTA position converts best.
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "post_listing_clicked",
        cta_source: source,
        page: "owners_landing",
      });
    } catch (_) { /* dataLayer might not be ready */ }
    nav("/list-property");
  };

  return (
    <div className="bg-white" data-testid="owners-landing-page">
      <SEO
        title="List your property free — verified tenants, zero brokerage"
        description="List your rental or sale property for FREE on To-Let Rentals. Reach verified tenants directly. No brokers, no commission. Get calls in 24 hours."
        path="/list-your-property"
        keywords="list your property, list property free, post rental ad, no brokerage listing, landlord listing India, owner property listing"
      />

      {/* ═══════════════════════════════ HERO ═══════════════════════════════ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#FBF6EC] to-white">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60'><path d='M0 30h60M30 0v60' stroke='%237A1E2A' stroke-width='0.5'/></svg>\")",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#7A1E2A]/10 border border-[#7A1E2A]/20 mb-6">
                <Sparkles size={14} className="text-[#7A1E2A]" />
                <span className="text-xs font-semibold text-[#7A1E2A] uppercase tracking-wider">
                  For property owners
                </span>
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1B0F14] leading-[1.05] mb-6">
                List your property.
                <br />
                <span className="text-[#7A1E2A]">Absolutely free.</span>
              </h1>

              <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-xl">
                Reach verified tenants directly. Zero brokerage, zero commission,
                zero hidden fees. Post in under 3 minutes — get calls within 24 hours.
              </p>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
                <Button
                  data-testid="owners-hero-cta"
                  onClick={() => postListing("hero")}
                  className="h-14 px-8 rounded-full bg-[#7A1E2A] hover:bg-[#5A1620] text-white text-base font-semibold shadow-lg shadow-[#7A1E2A]/20 group"
                >
                  Post FREE Listing
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle2 size={16} className="text-green-600" />
                  <span>No signup needed to start</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <IndianRupee size={16} className="text-[#C9A24A]" />
                  <span><strong className="text-[#1B0F14]">₹0</strong> brokerage forever</span>
                </div>
                <div className="flex items-center gap-2">
                  <BadgeCheck size={16} className="text-[#C9A24A]" />
                  <span><strong className="text-[#1B0F14]">Verified</strong> owner badge</span>
                </div>
                <div className="flex items-center gap-2">
                  <PhoneCall size={16} className="text-[#C9A24A]" />
                  <span>Direct <strong className="text-[#1B0F14]">tenant calls</strong></span>
                </div>
              </div>
            </div>

            {/* Right-side visual — clean listing preview card */}
            <div className="lg:col-span-2 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#7A1E2A]/10 border border-[#EFE3D0] bg-white transform lg:rotate-2 hover:rotate-0 transition-transform duration-500">
                <img
                  src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&h=600&fit=crop"
                  alt="Beautiful home interior"
                  className="w-full h-56 object-cover"
                />
                <div className="absolute top-4 left-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white shadow-md">
                  <BadgeCheck size={14} className="text-[#C9A24A]" />
                  <span className="text-xs font-bold text-[#7A1E2A]">Verified Owner</span>
                </div>
                <div className="p-5">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Your listing preview</p>
                  <p className="font-display text-xl text-[#1B0F14] mb-1">Spacious 2BHK near metro</p>
                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                    <MapPin size={12} />
                    <span>Koramangala, Bengaluru</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <p className="font-display text-2xl text-[#7A1E2A]">₹25,000<span className="text-sm text-gray-500 font-sans">/mo</span></p>
                    <div className="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2 py-1 rounded-full">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      12 tenants viewing
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 bg-white px-4 py-3 rounded-2xl shadow-xl border border-[#EFE3D0] hidden lg:block">
                <p className="text-xs text-gray-500 uppercase tracking-wider">This week</p>
                <p className="font-display text-lg text-[#7A1E2A]">2,400+ owners listed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════ AI WORKS FOR YOU ══════════════════════════ */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-12 pb-4" data-testid="owners-ai-band">
        <div className="rounded-3xl border border-[#EFE3D0] bg-gradient-to-br from-[#FBF6EC] to-white p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={16} className="text-[#C9A24A]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#C9A24A]">AI works for you</span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl text-[#1B0F14] mb-2">List once. Let AI bring you tenants.</h2>
          <p className="text-gray-600 text-sm max-w-2xl mb-6">
            You're busy — so our AI does the chasing. It replies to renters, books your visits and puts your listing in front of more people. You simply approve the ones you like.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: MessagesSquare, t: "AI answers enquiries 24/7", b: "Replies to tenant questions from your listing details — you never miss a lead." },
              { icon: CalendarCheck, t: "Visits booked for you", b: "Set your visiting hours once; get an instant WhatsApp + email alert to confirm." },
              { icon: TrendingUp, t: "Seen more on Google", b: "AI polishes your title & description so more renters actually find your home." },
            ].map((f) => (
              <div key={f.t} className="rounded-2xl bg-white border border-[#EFE3D0] p-4">
                <div className="w-10 h-10 rounded-full bg-[#FBF6EC] flex items-center justify-center mb-2.5">
                  <f.icon size={18} className="text-[#C9A24A]" />
                </div>
                <h3 className="font-semibold text-sm text-[#1B0F14] mb-1">{f.t}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{f.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <p className="label-eyebrow text-[#C9A24A] mb-2">Why owners choose us</p>
        <h2 className="font-display text-3xl sm:text-4xl text-[#1B0F14] mb-12 max-w-2xl">
          Everything a landlord needs. <span className="text-gray-500">Nothing you don't.</span>
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: IndianRupee,
              title: "Free forever",
              desc: "Post unlimited listings without paying a single rupee. We don't take a cut when your property gets rented or sold.",
            },
            {
              icon: BadgeCheck,
              title: "Verified owner badge",
              desc: "Upload your ID once and get a trust badge that increases inbound calls by 3x. Tenants prefer verified owners.",
            },
            {
              icon: PhoneCall,
              title: "Direct tenant contact",
              desc: "No middlemen. Tenants call and message you directly. Every lead is yours — negotiate on your own terms.",
            },
            {
              icon: Camera,
              title: "Upload up to 20 photos",
              desc: "Show off every angle. Add a video walkthrough. Great photos get 5x more inbound interest than text-only listings.",
            },
            {
              icon: Zap,
              title: "Live in under 3 minutes",
              desc: "Guided form with smart defaults. Fill it once, we optimize the rest for search — Google, Bing, and our home feed.",
            },
            {
              icon: Rocket,
              title: "Optional boost",
              desc: "Want extra reach? A one-time boost pins your listing to the top of your city. Only pay if you need to.",
            },
          ].map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="bg-white rounded-2xl p-6 border border-[#EFE3D0] hover:border-[#7A1E2A]/30 hover:shadow-lg hover:shadow-[#7A1E2A]/5 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-[#7A1E2A]/10 flex items-center justify-center mb-4">
                <Icon size={20} className="text-[#7A1E2A]" />
              </div>
              <h3 className="font-display text-xl text-[#1B0F14] mb-2">{title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════ HOW IT WORKS ══════════════════════════ */}
      <section className="bg-[#FBF6EC]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
          <p className="label-eyebrow text-[#C9A24A] mb-2">Three-step listing</p>
          <h2 className="font-display text-3xl sm:text-4xl text-[#1B0F14] mb-12 max-w-2xl">
            Live in <span className="text-[#7A1E2A]">under 3 minutes</span>. Yes, really.
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Fill the details", desc: "Property type, BHK, address, rent — no login needed to start. Photos, video walkthrough, amenities all in one guided form." },
              { step: "02", title: "Create your account", desc: "Only at the last step. Email or Google — 15 seconds. This is when we save your listing and send it for quick approval." },
              { step: "03", title: "Get calls in 24 hours", desc: "Verified tenants find you via Google search, our city pages, and the home feed. WhatsApp share included." },
            ].map(({ step, title, desc }) => (
              <div key={step} className="relative">
                <div className="font-display text-6xl text-[#C9A24A]/50 mb-3">{step}</div>
                <h3 className="font-display text-xl text-[#1B0F14] mb-2">{title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════ FINAL CTA ══════════════════════════ */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
        <ShieldCheck size={40} className="text-[#C9A24A] mx-auto mb-4" />
        <h2 className="font-display text-4xl sm:text-5xl text-[#1B0F14] mb-4 leading-tight">
          Ready to rent it out?
        </h2>
        <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
          Join thousands of owners who ditched brokers. Your property, your terms, your calls.
        </p>
        <Button
          data-testid="owners-bottom-cta"
          onClick={() => postListing("bottom")}
          className="h-14 px-10 rounded-full bg-[#7A1E2A] hover:bg-[#5A1620] text-white text-base font-semibold shadow-xl shadow-[#7A1E2A]/20"
        >
          Post FREE Listing
          <ArrowRight size={18} className="ml-2" />
        </Button>
        <p className="text-xs text-gray-500 mt-4">
          No credit card. No signup wall. Start filling now — sign in only when you're ready to publish.
        </p>
      </section>
    </div>
  );
}
